/**
 * Where auth tokens live. One module so moving to httpOnly cookies later is a
 * one-file change. deepdive-v2 parity for now: localStorage.
 *
 * Observable (`subscribe`) so the live tree reacts the moment a session ends —
 * sign-out, a failed refresh, or a sign-out in another tab (the `storage`
 * event) — instead of v2's hard `window.location` redirects.
 */
const ACCESS = 'dd.accessToken';
const REFRESH = 'dd.refreshToken';

function safe<T>(fn: () => T, fallback: T): T {
  try {
    return fn();
  } catch {
    return fallback;
  }
}

const listeners = new Set<() => void>();
const notify = () => listeners.forEach((listener) => listener());

function onStorage(event: StorageEvent) {
  // `key === null` is another tab calling `localStorage.clear()`.
  if (event.key === null || event.key === ACCESS || event.key === REFRESH) notify();
}

export const tokenStorage = {
  getAccessToken: () => safe(() => localStorage.getItem(ACCESS), null),
  getRefreshToken: () => safe(() => localStorage.getItem(REFRESH), null),
  setTokens(access: string, refresh: string) {
    safe(() => {
      localStorage.setItem(ACCESS, access);
      localStorage.setItem(REFRESH, refresh);
    }, undefined);
    notify();
  },
  clear() {
    safe(() => {
      localStorage.removeItem(ACCESS);
      localStorage.removeItem(REFRESH);
    }, undefined);
    notify();
  },
  hasSession: () => safe(() => Boolean(localStorage.getItem(ACCESS)), false),
  /** For `useSyncExternalStore`. Returns the unsubscribe. */
  subscribe(listener: () => void) {
    if (listeners.size === 0 && typeof window !== 'undefined')
      window.addEventListener('storage', onStorage);
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
      if (listeners.size === 0 && typeof window !== 'undefined')
        window.removeEventListener('storage', onStorage);
    };
  },
};
